import { useState } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "../../App";
import { styledToast as toast } from "@/components/ui/sonner";

// Official designations - grouped by department
const REVENUE_DESIGNATIONS = [
  { value: "tahsildar", labelKey: "designation.tahsildar" },
  { value: "deputy_tahsildar", labelKey: "designation.deputy_tahsildar" },
  { value: "shirestedar", labelKey: "designation.shirestedar" },
  { value: "revenue_inspector", labelKey: "designation.revenue_inspector" },
  { value: "village_accountant", labelKey: "designation.village_accountant" },
  { value: "first_division_clerk", labelKey: "designation.first_division_clerk" },
  { value: "second_division_clerk", labelKey: "designation.second_division_clerk" },
];

const SURVEY_DESIGNATIONS = [
  { value: "ddlr", labelKey: "designation.ddlr" },
  { value: "adlr", labelKey: "designation.adlr" },
  { value: "surveyor", labelKey: "designation.surveyor" },
];

const DEPARTMENTS = [
  { value: "revenue", labelKey: "department.revenue" },
  { value: "survey", labelKey: "department.survey" },
  { value: "gok", labelKey: "department.gok" },
];

export default function OfficialRegisterForm({ formData, setFormData, t }) {
  const [department, setDepartment] = useState("");
  const [designation, setDesignation] = useState("");
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const getDesignationsForDepartment = (dept) => {
    if (dept === "revenue") return REVENUE_DESIGNATIONS;
    if (dept === "survey") return SURVEY_DESIGNATIONS;
    return [];
  };

  const handleDepartmentChange = (dept) => {
    setDepartment(dept);
    setDesignation("");
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 10);
    setFormData({ ...formData, phone: value });
    if (errors.phone) {
      setErrors({ ...errors, phone: null });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = t('validation.nameRequired');
    } else if (formData.name.trim().length < 3) {
      newErrors.name = t('validation.nameMinLength');
    }
    
    if (!formData.email.trim()) {
      newErrors.email = t('validation.emailRequired');
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        newErrors.email = t('validation.emailInvalid');
      }
    }
    
    const phoneDigits = formData.phone.replace(/\D/g, '');
    if (!phoneDigits) {
      newErrors.phone = t('validation.phoneRequired');
    } else if (phoneDigits.length !== 10) {
      newErrors.phone = t('validation.phoneInvalid');
    }
    
    if (!formData.password) {
      newErrors.password = t('validation.passwordRequired');
    } else if (formData.password.length < 6) {
      newErrors.password = t('validation.passwordMinLength');
    } else if (!/[A-Z]/.test(formData.password)) {
      newErrors.password = t('validation.passwordUppercase');
    } else if (!/[0-9]/.test(formData.password)) {
      newErrors.password = t('validation.passwordNumber');
    }
    
    if (!department) {
      newErrors.department = t('validation.departmentRequired');
    }
    
    if (!designation) {
      newErrors.designation = t('validation.designationRequired');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      toast.error(t('validation.pleaseFixErrors'));
      return { success: false };
    }

    setLoading(true);
    try {
      const payload = { 
        ...formData, 
        role: "official", 
        designation, 
        department 
      };

      const response = await api.post("/auth/register", payload);
      return { 
        success: true, 
        token: response.data.access_token, 
        user: response.data.user 
      };
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        const errorMsg = detail.map(err => err.msg || err.message || JSON.stringify(err)).join(', ');
        toast.error(errorMsg);
      } else if (typeof detail === 'string') {
        toast.error(detail);
      } else {
        toast.error(t('common.anErrorOccurred'));
      }
      return { success: false };
    } finally {
      setLoading(false);
    }
  };

  const getButtonText = () => {
    return loading ? t('common.processing') : t('register.createAccount');
  };

  return {
    render: (
      <>
        <div>
          <Label htmlFor="name">{t('register.fullName')} <span className="text-red-500">*</span></Label>
          <Input
            id="name"
            type="text"
            required
            value={formData.name}
            onChange={(e) => {
              setFormData({ ...formData, name: e.target.value });
              if (errors.name) setErrors({ ...errors, name: null });
            }}
            className={`mt-1.5 ${errors.name ? 'border-red-500' : ''}`}
            data-testid="input-name"
          />
          {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
        </div>

        <div>
          <Label htmlFor="email">{t('login.email')} <span className="text-red-500">*</span></Label>
          <Input
            id="email"
            type="email"
            required
            value={formData.email}
            onChange={(e) => {
              setFormData({ ...formData, email: e.target.value });
              if (errors.email) setErrors({ ...errors, email: null });
            }}
            className={`mt-1.5 ${errors.email ? 'border-red-500' : ''}`}
            placeholder="official@example.com"
            data-testid="input-email"
          />
          {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
        </div>

        <div>
          <Label htmlFor="phone">{t('register.phoneRequired')} <span className="text-red-500">*</span></Label>
          <Input
            id="phone"
            type="tel"
            required
            value={formData.phone}
            onChange={handlePhoneChange}
            className={`mt-1.5 ${errors.phone ? 'border-red-500' : ''}`}
            placeholder="9876543210"
            maxLength={10}
            data-testid="input-phone"
          />
          {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
          <p className="text-slate-500 text-xs mt-1">{t('register.phoneHint')}</p>
        </div>

        <div>
          <Label htmlFor="password">{t('login.password')} <span className="text-red-500">*</span></Label>
          <Input
            id="password"
            type="password"
            required
            value={formData.password}
            onChange={(e) => {
              setFormData({ ...formData, password: e.target.value });
              if (errors.password) setErrors({ ...errors, password: null });
            }}
            className={`mt-1.5 ${errors.password ? 'border-red-500' : ''}`}
            placeholder="••••••••"
            data-testid="input-password"
          />
          {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password}</p>}
          <p className="text-slate-500 text-xs mt-1">{t('validation.passwordHint')}</p>
        </div>

        <div>
          <Label htmlFor="register-department">{t('register.selectDepartment')} <span className="text-red-500">*</span></Label>
          <Select value={department} onValueChange={(val) => {
            handleDepartmentChange(val);
            if (errors.department) setErrors({ ...errors, department: null });
          }}>
            <SelectTrigger className={`mt-1.5 ${errors.department ? 'border-red-500' : ''}`} data-testid="register-department-select">
              <SelectValue placeholder={t('register.selectDepartment')} />
            </SelectTrigger>
            <SelectContent>
              {DEPARTMENTS.map((d) => (
                <SelectItem key={d.value} value={d.value}>
                  {t(d.labelKey)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.department && <p className="text-red-500 text-xs mt-1">{errors.department}</p>}
        </div>

        {department && (
          <div>
            <Label htmlFor="designation">{t('designation.selectDesignation')} <span className="text-red-500">*</span></Label>
            <Select value={designation} onValueChange={(val) => {
              setDesignation(val);
              if (errors.designation) setErrors({ ...errors, designation: null });
            }}>
              <SelectTrigger className={`mt-1.5 ${errors.designation ? 'border-red-500' : ''}`} data-testid="designation-select">
                <SelectValue placeholder={t('designation.selectDesignation')} />
              </SelectTrigger>
              <SelectContent>
                {getDesignationsForDepartment(department).map((d) => (
                  <SelectItem key={d.value} value={d.value}>
                    {t(d.labelKey)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.designation && <p className="text-red-500 text-xs mt-1">{errors.designation}</p>}
          </div>
        )}
      </>
    ),
    handleSubmit,
    getButtonText,
    loading
  };
}
